/**
 * MM Product CRO — comportements ajoutés à la page produit.
 *
 * Périmètre volontairement restreint, pour ne rien devoir toucher dans
 * mm-main-product.js (qui gère déjà la variante, le prix et l'observer de la
 * barre sticky) :
 *   1. Colonne d'achat qui accompagne le scroll des visuels.
 *   2. Encart upsell rallonge : total du bouton d'achat + ajout au panier en 2 lignes.
 *   3. Paiement fractionné : recalcul quand la variante change.
 *   4. Panneau guide des tailles : fermeture au clavier (Échap) et masquage
 *      de la barre d'achat fixe tant qu'il est ouvert.
 *
 * Tout est dégradable : sans ce fichier, la colonne retombe sur le `top` fixe
 * du CSS (comportement historique du thème) et le bouton d'achat fonctionne
 * exactement comme avant.
 */
(function () {
	'use strict';

	var FORM = '.mm-product-form-container';
	var COLUMN = '.mm-product-form';
	var UPSELL = '[data-mm-upsell]';
	var GUIDE = '.mm-product-size-guide-container';

	/* ============================================ Colonne d'achat sticky */

	/**
	 * La colonne est plus haute que l'écran : un `top` fixe la figerait dès le
	 * chargement et sa fin (réassurance, accordéons) ne serait jamais
	 * atteignable, tandis qu'un défilement interne obligerait à scroller dans
	 * un cadre.
	 *
	 * On garde donc `position: sticky` et on pilote son `top` au scroll : à
	 * chaque pixel parcouru on décale le `top` d'autant, ce qui annule
	 * l'épinglage et fait défiler la colonne avec la page — jusqu'aux bornes.
	 * En descendant, la borne basse place sa dernière ligne en bas de l'écran ;
	 * en remontant, la borne haute la replace sous le header. Résultat : elle
	 * suit le lecteur dans les deux sens sans jamais rien cacher.
	 */
	function initStickyColumn() {
		var column = document.querySelector(COLUMN);
		if (!column) return;

		var GAP = 24; /* --spacing-4 */
		var DESKTOP = 991;

		var headerHeight = 0;
		var columnHeight = 0;
		var currentTop = null;
		var lastScroll = window.scrollY;
		var scheduled = false;

		function measure() {
			var raw = getComputedStyle(document.documentElement)
				.getPropertyValue('--header-height-scrolled');

			headerHeight = parseInt(raw, 10) || 0;
			columnHeight = column.offsetHeight;
		}

		function release() {
			if (currentTop === null) return;
			column.style.top = '';
			currentTop = null;
		}

		function update() {
			scheduled = false;

			var scroll = window.scrollY;
			var delta = scroll - lastScroll;
			lastScroll = scroll;

			/* Mobile : la colonne est en flux normal, rien à piloter. */
			if (window.innerWidth < DESKTOP) {
				release();
				return;
			}

			var maxTop = headerHeight + GAP;
			var minTop = window.innerHeight - columnHeight - GAP;

			/* La colonne tient dans l'écran : le sticky CSS suffit. */
			if (minTop >= maxTop) {
				release();
				return;
			}

			if (currentTop === null) currentTop = maxTop;

			currentTop = Math.min(maxTop, Math.max(minTop, currentTop - delta));
			column.style.top = Math.round(currentTop) + 'px';
		}

		function request() {
			if (scheduled) return;
			scheduled = true;
			window.requestAnimationFrame(update);
		}

		measure();

		window.addEventListener('scroll', request, { passive: true });
		window.addEventListener('resize', function () {
			measure();
			request();
		}, { passive: true });

		/* Les accordéons et l'encart rallonge changent la hauteur de la colonne :
		   on remesure plutôt que de lire offsetHeight à chaque frame de scroll. */
		if (window.ResizeObserver) {
			new ResizeObserver(function () {
				measure();
				request();
			}).observe(column);
		}

		update();
	}

	/* ------------------------------------------------------- Format prix */

	/* Reproduit `money` de Liquid à partir du money_format de la boutique,
	   pour que les montants calculés en JS s'écrivent exactement comme ceux
	   rendus par Liquid (séparateurs, décimales, place du symbole). */
	function groupDigits(integer, separator) {
		return integer.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
	}

	function amountFor(cents, name) {
		var value = (cents / 100).toFixed(name.indexOf('no_decimals') !== -1 ? 0 : 2);
		var parts = value.split('.');
		var integer = parts[0];
		var decimals = parts[1] || '';

		/* Comme le filtre Liquid `money_without_trailing_zeros` utilisé sur la
		   fiche : pas de centimes quand ils sont nuls (195€, mais 49,90€). */
		if (/^0+$/.test(decimals)) decimals = '';

		if (name.indexOf('comma_separator') !== -1) {
			return groupDigits(integer, '.') + (decimals ? ',' + decimals : '');
		}
		if (name.indexOf('space_separator') !== -1) {
			return groupDigits(integer, ' ') + (decimals ? ',' + decimals : '');
		}
		if (name.indexOf('apostrophe_separator') !== -1) {
			return groupDigits(integer, "'") + (decimals ? '.' + decimals : '');
		}
		return groupDigits(integer, ',') + (decimals ? '.' + decimals : '');
	}

	function formatMoney(cents) {
		var format = (window.mmMoneyFormat || '{{amount}}').replace(/<[^>]*>/g, '');

		var out = format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, name) {
			return amountFor(cents, name);
		});

		/* La fiche affiche `money_without_trailing_zeros | replace: '.', ','`. On s'aligne, mais
		   uniquement sur le point décimal : un séparateur de milliers reste intact. */
		return out.replace(/\.(\d{2})(?!\d)/, ',$1');
	}

	/* ---------------------------------------------------------------- Prix */

	function upsellInput() {
		return document.querySelector(UPSELL);
	}

	function basePrice() {
		var el = document.querySelector('.mm-product-price[data-price]');
		if (!el) return 0;
		return parseInt(el.getAttribute('data-price'), 10) || 0;
	}

	/* Réécrit le total du CTA et l'échéance du paiement fractionné.
	   On ne touche au DOM que si la valeur change : sans ça, le MutationObserver
	   ci-dessous se redéclencherait en boucle sur ses propres écritures. */
	function refreshTotals() {
		var base = basePrice();
		if (!base) return;

		var input = upsellInput();
		var extra = input && input.checked ? parseInt(input.getAttribute('data-upsell-price'), 10) || 0 : 0;

		var total = ' — ' + formatMoney(base + extra);
		var totals = document.querySelectorAll('[data-mm-cta-total]');

		for (var i = 0; i < totals.length; i++) {
			if (totals[i].textContent.trim() !== total.trim()) totals[i].textContent = total;
		}

		var installments = document.querySelectorAll('[data-mm-installments]');

		for (var j = 0; j < installments.length; j++) {
			var node = installments[j];
			var count = parseInt(node.getAttribute('data-mm-installments'), 10);
			var template = node.getAttribute('data-mm-installments-template') || '';

			if (!count || count < 2 || !template) continue;

			var text = template
				.replace('[n]', String(count))
				.replace('[montant]', formatMoney(Math.round(base / count)));

			if (node.textContent.trim() !== text.trim()) node.textContent = text;
		}
	}

	/* ------------------------------------------------------------ Panier */

	/**
	 * Ajout au panier depuis la page produit.
	 * Si la rallonge est cochée, elle part d'abord dans une requête silencieuse,
	 * puis le produit principal est ajouté par la fonction du thème — c'est elle
	 * qui rafraîchit le panier et l'ouvre. Si la rallonge échoue, le produit
	 * principal est ajouté quand même.
	 */
	window.mmAddProductWithUpsell = function (variantId, options) {
		var opts = options || { openCart: true };
		var input = upsellInput();
		var upsellId = input && input.checked ? input.getAttribute('data-upsell-variant') : null;

		var addMain = function () {
			return window.mmAddProductToCart(variantId, opts);
		};

		if (!upsellId || typeof window.mmAPIRequest !== 'function') return addMain();

		return window.mmAPIRequest('POST', 'cart/add.js', {
			items: [{ id: Number(upsellId), quantity: 1 }]
		}).then(addMain, addMain);
	};

	/* ------------------------------------------- Panneau guide des tailles */

	function guidePanel() {
		return document.querySelector(GUIDE);
	}

	function guideIsOpen() {
		var panel = guidePanel();
		return !!panel && panel.classList.contains('open');
	}

	/* Le panneau et la barre d'achat fixe se recouvrent : on pose une classe sur
	   <body> pour que le CSS puisse masquer la barre pendant l'ouverture. */
	function syncGuideState() {
		document.body.classList.toggle('mm-size-guide-open', guideIsOpen());
	}

	function watchGuide() {
		var panel = guidePanel();
		if (!panel) return;

		new MutationObserver(syncGuideState).observe(panel, {
			attributes: true,
			attributeFilter: ['class']
		});

		syncGuideState();
	}

	/* --------------------------------------------------------------- Init */

	function watchForm() {
		var form = document.querySelector(FORM);
		if (!form) return;

		var scheduled = false;
		var observer = new MutationObserver(function () {
			if (scheduled) return;
			scheduled = true;
			window.requestAnimationFrame(function () {
				scheduled = false;
				refreshTotals();
			});
		});

		observer.observe(form, { childList: true, subtree: true });
	}

	function init() {
		if (!document.querySelector(FORM)) return;

		document.addEventListener('change', function (event) {
			if (event.target && event.target.matches && event.target.matches(UPSELL)) refreshTotals();
		});

		document.addEventListener('keydown', function (event) {
			if (event.key !== 'Escape' && event.key !== 'Esc') return;
			if (!guideIsOpen() || typeof window.mmOpenSizeGuide !== 'function') return;
			window.mmOpenSizeGuide();
		});

		initStickyColumn();
		watchForm();
		watchGuide();
		refreshTotals();
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}

	document.addEventListener('shopify:section:load', init);
})();